import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Joyride, { CallBackProps, EVENTS, STATUS, Step, TooltipRenderProps, Styles } from 'react-joyride';
import { PiArrowRightLight, PiArrowLeftLight, PiXLight, PiCheckLight } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import { setGuidanceEnabled } from '../../../store/actions/TourActions';

interface FeatureTourProps {
  steps: Step[];
  run: boolean;
  onCallback: (data: CallBackProps) => void;
  /** Show step counter (e.g. "2 of 5"). Defaults to true. */
  showProgress?: boolean;
  /** Continuous mode - auto-advance between steps. Defaults to true. */
  continuous?: boolean;
  /** Show the skip button. Defaults to true. */
  showSkipButton?: boolean;
  /** Allow clicking the overlay to dismiss. Defaults to true. */
  disableOverlayClose?: boolean;
  /** Called before each step renders - use for tab switching, data loading, etc. */
  onBeforeStep?: (step: Step, index: number) => void | Promise<void>;
  /**
   * Let Joyride handle scrolling & scroll-parent position fixes natively.
   * Use this when targets live inside a scrollable container (e.g. a side panel).
   * Defaults to false (manual scroll via scrollIntoView).
   */
  nativeScrolling?: boolean;
  /**
   * Disable overlay/spotlight and highlight the target with a ring instead.
   * Use for tours inside CSS-transformed containers (e.g. side panels) where
   * Joyride's fixed-position overlay breaks. Tooltip portals to body.
   */
  disableOverlay?: boolean;
}

/**
 * Custom tooltip matching PONS design system.
 * Uses Tailwind classes, Poppins font, and Phosphor icons.
 */
const TourTooltip: React.FC<TooltipRenderProps & { showProgress?: boolean }> = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
  size,
  isLastStep,
  showProgress = true,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const hasMultipleSteps = size > 1;

  // Global opt-out: turn off guidance everywhere AND close the current tour.
  const turnOffTips = (e: React.MouseEvent) => {
    dispatch(setGuidanceEnabled(false));
    closeProps?.onClick?.(e as unknown as React.MouseEvent<HTMLElement>);
  };

  return (
    <div
      {...tooltipProps}
      className="bg-surface rounded-lg shadow-xl border border-hairline max-w-sm w-[340px] overflow-hidden animate-fade-in"
      style={{ fontFamily: 'Poppins, system-ui, sans-serif' }}
    >
      {/* Header with close button */}
      <div className="flex items-start justify-between px-5 pt-4 pb-0">
        <div className="flex-1 min-w-0">
          {step.title && (
            <h3 className="text-sm font-semibold text-fg-strong leading-snug">
              {typeof step.title === 'string' ? t(step.title) : step.title}
            </h3>
          )}
        </div>
        <button
          {...closeProps}
          className="flex-shrink-0 ms-3 p-1 rounded-md text-fg-muted hover:text-fg-body hover:bg-surface-sunken transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
          aria-label={t('Close tour')}
        >
          <PiXLight className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="px-5 pt-2 pb-4">
        {step.content && (
          <div className="text-sm text-fg-body leading-relaxed">
            {typeof step.content === 'string' ? t(step.content) : step.content}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-5 py-3 border-t border-hairline bg-surface-sunken">
        {/* Left side: skip or step counter */}
        <div className="flex items-center gap-3">
          {skipProps && hasMultipleSteps && !isLastStep && (
            <button
              {...skipProps}
              className="px-3 py-1.5 text-xs font-medium text-fg-body bg-surface border border-hairline rounded-md hover:bg-surface-sunken hover:text-fg-strong transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
            >
              {t('Skip')}
            </button>
          )}
          {showProgress && hasMultipleSteps && (
            <div className="flex items-center gap-1.5">
              {Array.from({ length: size }).map((_, i) => (
                <div
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition-colors duration-200 ${
                    i === index
                      ? 'bg-primary-600 dark:bg-white'
                      : i < index
                        ? 'bg-primary-300 dark:bg-gray-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Right side: navigation buttons */}
        <div className="flex items-center gap-2">
          {index > 0 && (
            <button
              {...backProps}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-fg-body bg-surface border border-hairline rounded-md hover:bg-surface-sunken transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
            >
              <PiArrowLeftLight className="w-3.5 h-3.5 rtl:-scale-x-100" />
              {t('Back')}
            </button>
          )}
          {continuous && (
            <button
              {...primaryProps}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 dark:bg-white dark:text-gray-900 rounded-md hover:bg-primary-700 dark:hover:bg-gray-100 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
            >
              {isLastStep ? (
                <>
                  {t('Got it')}
                  <PiCheckLight className="w-3.5 h-3.5" />
                </>
              ) : (
                <>
                  {t('Next')}
                  <PiArrowRightLight className="w-3.5 h-3.5 rtl:-scale-x-100" />
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Global opt-out - stop guided tips everywhere (re-enable in the Help menu) */}
      <button
        type="button"
        onClick={turnOffTips}
        className="w-full text-center text-[11px] text-fg-muted hover:text-fg-body py-2 border-t border-hairline transition-colors duration-150 focus:outline-none"
      >
        {t('Turn off guided tips')}
      </button>
    </div>
  );
};

const FeatureTourInner: React.FC<FeatureTourProps> = ({
  steps,
  run,
  onCallback,
  showProgress = true,
  continuous = true,
  showSkipButton = true,
  disableOverlayClose = false,
  onBeforeStep,
  nativeScrolling = false,
  disableOverlay = false,
}) => {
  // Track the active target's viewport rect for the custom spotlight overlay
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);
  // Track the current step's target element for scroll-based re-measurement
  const currentTargetRef = useRef<HTMLElement | null>(null);
  // rAF handle for debouncing spotlight measurements
  const rafRef = useRef(0);
  // Proxy elements created for oversized targets (cleaned up when tour stops)
  const proxiesRef = useRef<HTMLDivElement[]>([]);
  // Map from proxy selector → original target selector for spotlight
  const proxyOriginalMapRef = useRef<Map<string, string | HTMLElement>>(new Map());
  // Resolved steps including proxy targets for oversized elements
  const [resolvedSteps, setResolvedSteps] = useState<Step[]>([]);

  /** Remove all proxy anchor elements from the DOM */
  const cleanupProxies = useCallback(() => {
    proxiesRef.current.forEach(el => el.remove());
    proxiesRef.current = [];
    proxyOriginalMapRef.current.clear();
  }, []);

  /**
   * Build the final step list. For disableOverlay mode, measure each target.
   * Targets taller than 50% of the viewport get a tiny fixed proxy element
   * pinned at their top edge so the tooltip always appears in a visible spot.
   * Smaller targets just get placement: 'auto'.
   */
  useEffect(() => {
    if (!run || steps.length === 0) {
      cleanupProxies();
      setResolvedSteps([]);
      return;
    }

    if (!disableOverlay) {
      setResolvedSteps(steps);
      return;
    }

    // Wait one frame so the DOM has rendered the target elements
    const raf = requestAnimationFrame(() => {
      cleanupProxies();
      const processed = steps.map((step, i) => {
        const el =
          typeof step.target === 'string'
            ? document.querySelector<HTMLElement>(step.target)
            : step.target;
        if (!el) return { ...step, placement: 'auto' as const };

        const rect = el.getBoundingClientRect();
        if (rect.height > window.innerHeight * 0.5) {
          // Create a tiny invisible anchor at the top of the visible target area
          const proxy = document.createElement('div');
          const proxyId = `tour-proxy-${i}-${Date.now()}`;
          proxy.setAttribute('data-tour-proxy', proxyId);
          const visibleTop = Math.max(rect.top, 0);
          proxy.style.cssText = `position:fixed;top:${visibleTop + 4}px;left:${rect.left}px;width:${rect.width}px;height:2px;pointer-events:none;`;
          document.body.appendChild(proxy);
          proxiesRef.current.push(proxy);

          const proxySelector = `[data-tour-proxy="${proxyId}"]`;
          proxyOriginalMapRef.current.set(proxySelector, step.target);
          return { ...step, target: proxySelector, placement: 'bottom' as const };
        }
        return { ...step, placement: 'auto' as const };
      });
      setResolvedSteps(processed);
    });

    return () => cancelAnimationFrame(raf);
  }, [steps, run, disableOverlay, cleanupProxies]);

  // Clean up proxies on unmount
  useEffect(() => cleanupProxies, [cleanupProxies]);

  /** Measure the target element and update the spotlight rect (debounced via rAF).
   *  For targets taller than the viewport, clamp the rect to the visible portion
   *  so the spotlight ring stays meaningful and the tooltip can anchor properly. */
  const measureTarget = useCallback((el: HTMLElement | null) => {
    if (!el) return; // Keep last known position - avoids flicker on transient unmounts
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const raw = el.getBoundingClientRect();
      // Element hidden/removed - keep last known position
      if (raw.width === 0 && raw.height === 0) return;

      // Clamp to the visible portion of the viewport so oversized targets
      // (full-page containers) don't push the spotlight/tooltip off-screen.
      const top = Math.max(raw.top, 0);
      const left = Math.max(raw.left, 0);
      const bottom = Math.min(raw.bottom, window.innerHeight);
      const right = Math.min(raw.right, window.innerWidth);
      const width = Math.max(right - left, 0);
      const height = Math.max(bottom - top, 0);

      const clamped = { top, left, width, height, bottom, right } as DOMRect;

      setSpotlightRect(prev => {
        // Skip update if position hasn't meaningfully changed (< 1px)
        if (
          prev &&
          Math.abs(prev.top - clamped.top) < 1 &&
          Math.abs(prev.left - clamped.left) < 1 &&
          Math.abs(prev.width - clamped.width) < 1 &&
          Math.abs(prev.height - clamped.height) < 1
        ) {
          return prev;
        }
        return clamped;
      });
    });
  }, []);

  // Clean up rAF on unmount
  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  // Listen for scroll & resize events to keep the spotlight positioned correctly
  useEffect(() => {
    if (!disableOverlay || !run) return;

    const onScrollOrResize = () => {
      if (currentTargetRef.current) {
        measureTarget(currentTargetRef.current);
      }
    };
    // Capture phase so we catch scrolls on any nested container
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [disableOverlay, run, measureTarget]);

  // Reset spotlight when tour stops running
  useEffect(() => {
    if (!run) {
      cancelAnimationFrame(rafRef.current);
      currentTargetRef.current = null;
      setSpotlightRect(null);
    }
  }, [run]);

  // Minimal Joyride styles - we override rendering with our custom tooltip
  // but still need spotlight/overlay styles
  const joyrideStyles: Partial<Styles> = useMemo(
    () => ({
      options: {
        zIndex: 10000,
        arrowColor: 'transparent',
      },
      overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.35)',
      },
      spotlight: {
        borderRadius: 12,
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.35)',
      },
      // Hide default beacon - we handle auto-start via the hook
      beacon: {
        display: 'none',
      },
      beaconInner: {
        display: 'none',
      },
      beaconOuter: {
        display: 'none',
      },
    }),
    [],
  );

  // Scroll the target element into view inside its nearest scrollable ancestor
  // instead of letting Joyride scroll the document/page.
  const handleCallback = useCallback(
    (data: CallBackProps) => {
      if (data.type === EVENTS.STEP_BEFORE) {
        // Allow consumers to switch tabs / load data before the step renders
        if (onBeforeStep) {
          onBeforeStep(data.step, data.index);
        }

        // When disableOverlay is on the user is already on the correct tab —
        // never scroll, let Popper handle tooltip positioning.
        // For normal (overlay) tours, only scroll when the target is off-screen.
        if (!disableOverlay && !nativeScrolling && data.step?.target) {
          const el =
            typeof data.step.target === 'string'
              ? document.querySelector<HTMLElement>(data.step.target)
              : data.step.target;

          if (el) {
            const rect = el.getBoundingClientRect();
            const isTopVisible = rect.top >= 0 && rect.top < window.innerHeight;
            if (!isTopVisible) {
              el.scrollIntoView({ behavior: 'auto', block: 'start' });
            }
          }
        }

        // When overlay is disabled, measure the target for our custom spotlight.
        // If the step uses a proxy (oversized target), resolve the ORIGINAL target
        // so the spotlight highlights the actual content, not the tiny proxy.
        if (disableOverlay && data.step?.target) {
          let spotlightTarget = data.step.target;
          if (typeof spotlightTarget === 'string' && proxyOriginalMapRef.current.has(spotlightTarget)) {
            spotlightTarget = proxyOriginalMapRef.current.get(spotlightTarget)!;
          }
          const spotlightEl =
            typeof spotlightTarget === 'string'
              ? document.querySelector<HTMLElement>(spotlightTarget)
              : spotlightTarget;
          currentTargetRef.current = spotlightEl;
          if (spotlightEl) {
            requestAnimationFrame(() => measureTarget(spotlightEl));
          }
        }
      }

      // Clean up spotlight when tour ends
      if (
        disableOverlay &&
        (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED)
      ) {
        currentTargetRef.current = null;
        setSpotlightRect(null);
        cleanupProxies();
      }

      onCallback(data);
    },
    [onCallback, onBeforeStep, nativeScrolling, disableOverlay, measureTarget, cleanupProxies],
  );

  // Stable tooltip component - prevents Joyride from re-mounting the tooltip
  // on every parent re-render (major source of flicker during API calls)
  const StableTooltip = useMemo(
    () => {
      const Comp: React.FC<TooltipRenderProps> = (props) => (
        <TourTooltip {...props} showProgress={showProgress} />
      );
      Comp.displayName = 'StableTooltip';
      return Comp;
    },
    [showProgress],
  );

  // resolvedSteps is computed via useEffect above (DOM-aware, handles proxy targets)

  if (!run || resolvedSteps.length === 0) {
    return null;
  }

  return (
    <>
      <Joyride
        steps={resolvedSteps}
        run={run}
        continuous={continuous}
        showSkipButton={showSkipButton}
        callback={handleCallback}
        styles={joyrideStyles}
        disableCloseOnEsc={false}
        disableOverlayClose={disableOverlayClose}
        disableOverlay={disableOverlay}
        disableScrolling={!nativeScrolling}
        scrollToFirstStep={nativeScrolling}
        scrollOffset={80}
        spotlightClicks={false}
        floaterProps={{
          ...(disableOverlay ? { portal: document.body } : {}),
          disableAnimation: false,
          styles: {
            arrow: {
              length: 8,
              spread: 16,
            },
          },
        }}
        tooltipComponent={StableTooltip}
      />
      {disableOverlay && spotlightRect && run
        && spotlightRect.width > 8 && spotlightRect.height > 8 && (() => {
        // Only draw the dark spotlight scrim when the target is meaningfully
        // on-screen. An off-screen / oversized target clamps to a sliver, which
        // would otherwise cover the whole screen with the cutout in the wrong
        // place (the "weird overlay").
        return createPortal(
          <div style={{ position: 'fixed', inset: 0, zIndex: 9990, pointerEvents: 'none' }}>
            <div
              style={{
                position: 'absolute',
                top: spotlightRect.top - 8,
                left: spotlightRect.left - 8,
                width: spotlightRect.width + 16,
                height: spotlightRect.height + 16,
                borderRadius: 12,
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.35)',
                pointerEvents: 'none',
              }}
            />
          </div>,
          document.body
        );
      })()}
    </>
  );
};

const FeatureTour = React.memo(FeatureTourInner);
export default FeatureTour;
